import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";

const ChatPage = () => {
  const { user, apiCall } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [pendingCompany, setPendingCompany] = useState(null);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);

  const messagesEndRef = useRef(null);

  const getSenderType = () => {
    const role = String(user?.role || user?.user_role || "").toLowerCase();

    if (
      role === "employee" ||
      role === "company" ||
      role === "company_owner" ||
      role === "admin" ||
      role === "support" ||
      role === "superadmin" ||
      role === "super_admin"
    ) {
      return "employee";
    }

    return "user";
  };

  const refreshUnreadCounter = () => {
    window.dispatchEvent(new Event("zootel-chat-unread-refresh"));
  };

  const markChatAsRead = async (chatId) => {
    if (!chatId || chatId.toString().startsWith("new-")) {
      return;
    }

    try {
      await apiCall(`/chats/${chatId}/read`, {
        method: "POST",
      });

      refreshUnreadCounter();
    } catch (error) {
      console.error("Mark chat as read error:", error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const normalizeImageUrl = (url) => {
    if (!url || typeof url !== "string") {
      return "";
    }

    const cleanUrl = url.trim();

    if (
      !cleanUrl ||
      cleanUrl === "null" ||
      cleanUrl === "undefined" ||
      cleanUrl === "/" ||
      cleanUrl.length < 5
    ) {
      return "";
    }

    if (
      cleanUrl.startsWith("http://") ||
      cleanUrl.startsWith("https://") ||
      cleanUrl.startsWith("data:") ||
      cleanUrl.startsWith("blob:")
    ) {
      return cleanUrl;
    }

    if (cleanUrl.startsWith("/uploads/")) {
      return cleanUrl;
    }

    if (cleanUrl.startsWith("uploads/")) {
      return `/${cleanUrl}`;
    }

    return "";
  };

  const getCompanyLogo = (chat) => {
    const rawLogo =
      chat?.logo_url ||
      chat?.company_logo_url ||
      chat?.company_logo ||
      chat?.logo ||
      chat?.company?.logo_url ||
      chat?.company?.logo ||
      "";

    return normalizeImageUrl(rawLogo);
  };

  const getCompanyName = (chat) => {
    return (
      chat?.company_name ||
      chat?.companyName ||
      chat?.company?.name ||
      chat?.subject ||
      `Company ${chat?.company_id?.slice(0, 6) || ""}`
    );
  };

  const CompanyAvatar = ({ chat, size = "md", active = false, dark = false }) => {
    const logo = getCompanyLogo(chat);
    const name = getCompanyName(chat);
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
      setImageFailed(false);
    }, [logo]);

    const sizeClass = size === "sm" ? "h-9 w-9" : "h-10 w-10";
    const textClass = size === "sm" ? "text-[13px]" : "text-[16px]";
    const shouldShowLogo = Boolean(logo) && !imageFailed;

    return (
      <div
        className={`${sizeClass} rounded-xl flex items-center justify-center shrink-0 overflow-hidden border border-white shadow-sm ${
          dark ? "bg-[#0A1128]" : active ? "bg-orange-50" : "bg-slate-100"
        }`}
      >
        {shouldShowLogo ? (
          <img
            src={logo}
            alt={name}
            onError={() => setImageFailed(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <span
            className={`${textClass} font-black ${
              dark ? "text-white" : "text-[#F65814]"
            }`}
          >
            {name?.charAt(0)?.toUpperCase() || "C"}
          </span>
        )}
      </div>
    );
  };

  const loadUserChats = async () => {
    if (!user) return;

    try {
      const data = await apiCall("/chats");
      const loadedChats = data?.data || [];

      setChats(loadedChats);
    } catch (error) {
      console.error("Load user chats error:", error);
      setChats([]);
    }
  };

  const loadChatMessages = async (chatId) => {
    try {
      const data = await apiCall(`/chats/${chatId}/messages`);
      const loadedMessages = data?.data?.messages || [];

      setMessages(loadedMessages);
    } catch (error) {
      console.error("Load chat messages error:", error);
      setMessages([]);
    }
  };

  const handleSelectChat = (chat) => {
    setActiveChat(chat);
    markChatAsRead(chat.id);
  };

  const handleBackToChats = () => {
    setActiveChat(null);
  };

  const handleCloseChat = () => {
    if (activeChat?.id) {
      markChatAsRead(activeChat.id);
    }

    setIsOpen(false);
  };

  useEffect(() => {
    const handleToggleChat = (event) => {
      const chatTarget = event?.detail || null;

      setPendingCompany(chatTarget);
      setIsOpen((prev) => (chatTarget ? true : !prev));
    };

    window.addEventListener("open-zootel-chat", handleToggleChat);

    return () => {
      window.removeEventListener("open-zootel-chat", handleToggleChat);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [messages, isOpen]);

  useEffect(() => {
    if (user && isOpen) {
      loadUserChats();
    }
  }, [user, isOpen]);

  useEffect(() => {
    if (!pendingCompany || !chats.length) return;

    const foundChat = chats.find(
      (chat) => chat.company_id === pendingCompany.companyId,
    );

    if (foundChat) {
      setActiveChat(foundChat);
      markChatAsRead(foundChat.id);
      setPendingCompany(null);
    }
  }, [pendingCompany, chats]);

  useEffect(() => {
    if (!activeChat || activeChat.id?.toString().startsWith("new-")) {
      return undefined;
    }

    markChatAsRead(activeChat.id);
    loadChatMessages(activeChat.id);

    const interval = setInterval(() => {
      loadChatMessages(activeChat.id);
    }, 5000);

    return () => clearInterval(interval);
  }, [activeChat]);

  useEffect(() => {
    if (pendingCompany && !activeChat) {
      setActiveChat({
        id: `new-${pendingCompany.companyId}`,
        company_id: pendingCompany.companyId,
        companyName: pendingCompany.companyName,
        logo_url:
          pendingCompany.logo_url ||
          pendingCompany.company_logo_url ||
          pendingCompany.companyLogo ||
          "",
        isDraft: true,
      });

      setMessages([]);
      setPendingCompany(null);
    }
  }, [pendingCompany, activeChat]);

  const handleSendMessage = async (event) => {
    event.preventDefault();

    if (!message.trim() || !activeChat) return;

    const text = message.trim();
    const senderType = getSenderType();

    setMessage("");

    const optimisticMessage = {
      id: Date.now().toString(),
      sender_type: senderType,
      message_text: text,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, optimisticMessage]);

    try {
      let chatId = activeChat.id;

      if (activeChat.id.toString().startsWith("new-")) {
        const created = await apiCall("/chats", {
          method: "POST",
          body: JSON.stringify({
            company_id: activeChat.company_id,
            user_id: user.uid || user.id,
            initial_message: text,
          }),
        });

        const createdChat = created?.data;

        if (!createdChat?.id) {
          throw new Error("Unable to create chat");
        }

        chatId = createdChat.id;

        setActiveChat(createdChat);

        setChats((prev) => [
          createdChat,
          ...prev.filter((chat) => chat.id !== createdChat.id),
        ]);

        await loadUserChats();
      } else {
        await apiCall(`/chats/${chatId}/messages`, {
          method: "POST",
          body: JSON.stringify({
            chat_id: chatId,
            sender_type: senderType,
            message_text: text,
          }),
        });
      }

      await loadChatMessages(chatId);
      await markChatAsRead(chatId);
      refreshUnreadCounter();
    } catch (error) {
      setMessages((prev) =>
        prev.filter((msg) => msg.id !== optimisticMessage.id),
      );

      console.error("Send error:", error);
    }
  };

  const formatChatTime = (isoDate) => {
    if (!isoDate) return "";

    return new Date(isoDate).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!user || !isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-0 md:p-4">
      <div
        className="absolute inset-0 bg-[#01128]/60 backdrop-blur-sm animate-in fade-in duration-300"
        onClick={handleCloseChat}
      />
  
      <div className="relative w-full h-full md:max-w-[950px] md:h-[680px] md:max-h-[90vh] bg-white md:rounded-[32px] shadow-2xl flex overflow-hidden animate-in zoom-in-95 duration-500 ease-out">
        <div
          className={`${
            activeChat ? "hidden md:flex" : "flex"
          } w-full md:w-[300px] border-r border-gray-100 flex-col bg-[#F9FBFC]`}
        >
          <div className="p-5 md:p-6 pb-2 safe-top">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[#0A1128] text-xl font-black tracking-tight">
                Messages
              </h2>
  
              <button
                type="button"
                onClick={handleCloseChat}
                className="p-2 hover:bg-slate-50 hover:bg-[#FF5C35] bg-[#0A1128] rounded-xl shadow-xl shadow-slate-200 text-gray-300 hover:text-gray-900"
              >
                <XMarkIcon className="h-5 w-5 stroke-2" />
              </button>
  
              <ChatBubbleLeftRightIcon className="hidden md:block h-5 w-5 text-[#F65814]" />
            </div>
  
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
  
              <input
                type="text"
                placeholder="Search..."
                className="w-full bg-white border-none rounded-xl py-3 md:py-2.5 pl-9 pr-3 text-[13px] md:text-[12px] font-bold shadow-sm focus:ring-1 focus:ring-orange-200 placeholder:text-slate-300"
              />
            </div>
          </div>
  
          <div className="flex-1 overflow-y-auto px-2 pb-4 mt-4">
            <div className="space-y-1">
              {chats.map((chat) => {
                const isActive = activeChat?.id === chat.id;
  
                return (
                  <button
                    key={chat.id}
                    type="button"
                    onClick={() => handleSelectChat(chat)}
                    className={`w-full flex items-center gap-3 p-3.5 md:p-3 rounded-2xl transition-all text-left ${
                      isActive
                        ? "bg-white shadow-md ring-1 ring-black/5 scale-[1.02]"
                        : "hover:bg-white/60"
                    }`}
                  >
                    <CompanyAvatar chat={chat} active={isActive} />
  
                    <div className="flex-1 overflow-hidden">
                      <div className="flex justify-between items-center gap-2">
                        <h4
                          className={`text-[12px] md:text-[11px] font-black uppercase truncate ${
                            isActive ? "text-[#F65814]" : "text-[#0A1128]"
                          }`}
                        >
                          {getCompanyName(chat)}
                        </h4>
  
                        <span className="text-[9px] font-bold text-slate-300 shrink-0">
                          {formatChatTime(chat.last_message_at || chat.updated_at)}
                        </span>
                      </div>
  
                      <p className="text-[12px] md:text-[11px] text-slate-400 truncate font-medium leading-tight">
                        {chat.last_message?.message_text || "Начните диалог"}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
  
        <div
          className={`${
            activeChat ? "flex" : "hidden md:flex"
          } flex-1 flex-col bg-white w-full min-w-0`}
        >
          {activeChat ? (
            <>
              <div className="h-16 px-4 md:px-6 flex items-center justify-between border-b border-gray-50 flex-shrink-0 safe-top">
                <div className="flex items-center gap-3 min-w-0">
                <button
                  type="button"
                  onClick={handleBackToChats}
                  className="md:hidden h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-[#0A1128] hover:text-[#F65814] shrink-0 text-2xl font-black leading-none -translate-y-[2px]"
                  aria-label="Back to chats"
                >
                  ‹
                </button>
  
                  <CompanyAvatar chat={activeChat} size="sm" dark />
  
                  <div className="min-w-0">
                    <h3 className="text-[#0A1128] text-[12px] font-black uppercase tracking-widest leading-none truncate">
                      {getCompanyName(activeChat)}
                    </h3>
  
                    <p className="text-green-500 text-[9px] font-black uppercase mt-1">
                      Support Online
                    </p>
                  </div>
                </div>
  
                <button
                  type="button"
                  onClick={handleCloseChat}
                  className="p-2 text-slate-300 hover:text-[#0A1128] transition-all shrink-0"
                >
                  <XMarkIcon className="h-5 w-5 stroke-2" />
                </button>
              </div>
  
              <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-5 bg-[#FCFCFD]">
                {messages.map((msg) => {
                  const isOwnMessage = msg.sender_type === getSenderType();
  
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${
                        isOwnMessage ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div className="max-w-[86%] md:max-w-[75%]">
                        <div
                          className={`px-4 py-3 md:py-2.5 rounded-[20px] text-[14px] md:text-[13px] font-medium leading-snug shadow-sm ${
                            isOwnMessage
                              ? "bg-[#F65814] text-white rounded-tr-none"
                              : "bg-white text-[#0A1128] border border-slate-100 rounded-tl-none"
                          }`}
                        >
                          {msg.message_text}
                        </div>
  
                        <p
                          className={`text-[9px] font-black uppercase mt-1.5 tracking-wider text-slate-300 ${
                            isOwnMessage ? "text-right" : "text-left"
                          }`}
                        >
                          {formatChatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
  
                <div ref={messagesEndRef} />
              </div>
  
              <form
                onSubmit={handleSendMessage}
                className="p-3 md:p-4 bg-white border-t border-gray-50 flex items-center gap-2 md:gap-3 safe-bottom"
              >
                <input
                  type="text"
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder={
                    activeChat?.isDraft
                      ? "Напишите первое сообщение компании..."
                      : "Type a message..."
                  }
                  className="flex-1 bg-slate-50 border-none rounded-xl py-3.5 md:py-3 px-4 md:px-5 text-[13px] md:text-[12px] font-bold focus:ring-1 focus:ring-orange-100 placeholder:text-slate-300 shadow-inner min-w-0"
                />
  
                <button
                type="submit"
                disabled={!message.trim()}
                className="p-2 hover:bg-slate-50 hover:bg-[#FF5C35] bg-[#0A1128] rounded-xl shadow-xl shadow-slate-200 text-gray-300 hover:text-gray-900"
                aria-label="Send message"
              >
                <PaperAirplaneIcon
                  className="h-6 w-6 -rotate-45"
                  stroke="currentColor"
                  strokeWidth={1.5}
                />
              </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-10 opacity-30">
              <ChatBubbleLeftRightIcon className="h-12 w-12 text-slate-200 mb-4" />
  
              <p className="text-[10px] font-black uppercase tracking-[0.2em]">
                Select a message
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatPage;